/*!
 * ChartEngine - Four Pillars computation core for bazi-calculator.org
 *
 * Dual-basis design:
 *   Year / Month pillars and the luck cycles run on China-standard time,
 *   the same basis the solar-term tables are expressed in.
 *   Day / Hour pillars run on true solar time, so the hour branch keeps
 *   birthplace precision.
 * Ten Gods (stems and hidden stems) are always recomputed against the true
 * Day Master rather than inherited from the calendar library, because the
 * two bases can land on different calendar days.
 */
(function (root) {
  'use strict';

  var STEM_ELEMENT = {
    '甲': 'Wood', '乙': 'Wood', '丙': 'Fire', '丁': 'Fire',
    '戊': 'Earth', '己': 'Earth', '庚': 'Metal', '辛': 'Metal',
    '壬': 'Water', '癸': 'Water'
  };
  var YANG_STEMS = ['甲', '丙', '戊', '庚', '壬'];
  var CYCLE = ['Wood', 'Fire', 'Earth', 'Metal', 'Water'];
  var HIDDEN_WEIGHTS = [1.0, 0.5, 0.3];
  var GAN = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
  var ZHI = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];

  /* Strength model, ported unchanged from the production calculator so that
     this site, the paid pipeline and the Chinese site cannot disagree. */
  var BRANCH_SEASON = {
    '寅':'spring', '卯':'spring', '辰':'lateSummer',
    '巳':'summer', '午':'summer', '未':'lateSummer',
    '申':'autumn', '酉':'autumn', '戌':'lateSummer',
    '亥':'winter', '子':'winter', '丑':'lateSummer'
  };
  var SEASON_SCORE = {
    spring:     { Wood:4, Fire:3, Earth:0, Metal:1, Water:2 },
    summer:     { Wood:2, Fire:4, Earth:3, Metal:0, Water:1 },
    lateSummer: { Wood:1, Fire:2, Earth:4, Metal:3, Water:0 },
    autumn:     { Wood:0, Fire:1, Earth:2, Metal:4, Water:3 },
    winter:     { Wood:3, Fire:0, Earth:1, Metal:2, Water:4 }
  };
  var FEEDS    = { Wood:'Fire', Fire:'Earth', Earth:'Metal', Metal:'Water', Water:'Wood' };
  var FED_BY   = { Wood:'Water', Fire:'Wood', Earth:'Fire', Metal:'Earth', Water:'Metal' };
  var CONTROLS = { Wood:'Earth', Fire:'Metal', Earth:'Water', Metal:'Wood', Water:'Fire' };
  var CURBED   = { Wood:'Metal', Fire:'Water', Earth:'Wood', Metal:'Fire', Water:'Earth' };

  function stemElement(s) { return STEM_ELEMENT[s] || ''; }
  function stemPolarity(s) { return YANG_STEMS.indexOf(s) >= 0 ? 'Yang' : 'Yin'; }

  /* Ten God of `other` as seen from `self`; both are heavenly stems. */
  function tenGod(self, other) {
    if (!self || !other) return '';
    var me = stemElement(self), it = stemElement(other);
    if (!me || !it) return '';
    var m = CYCLE.indexOf(me), o = CYCLE.indexOf(it);
    var alike = stemPolarity(self) === stemPolarity(other);
    if (me === it) return alike ? '比肩' : '劫财';
    if ((m + 1) % 5 === o) return alike ? '食神' : '伤官';
    if ((m + 2) % 5 === o) return alike ? '偏财' : '正财';
    if ((o + 1) % 5 === m) return alike ? '偏印' : '正印';
    if ((o + 2) % 5 === m) return alike ? '七杀' : '正官';
    return '';
  }

  function ask(obj, method, fallback) {
    try {
      if (obj && typeof obj[method] === 'function') return obj[method]();
    } catch (e) { /* library edge case; fall through to the default */ }
    return fallback;
  }

  /* Element tally across the assembled pillars, hidden stems weighted. */
  function tallyElements(pillars) {
    var zh2en = { '木': 'wood', '火': 'fire', '土': 'earth', '金': 'metal', '水': 'water' };
    var zhOf = {
      '甲': '木', '乙': '木', '丙': '火', '丁': '火', '戊': '土',
      '己': '土', '庚': '金', '辛': '金', '壬': '水', '癸': '水'
    };
    var out = { wood: 0, fire: 0, earth: 0, metal: 0, water: 0 };
    ['year', 'month', 'day', 'hour'].forEach(function (k) {
      var p = pillars[k];
      if (!p) return;
      var e = zh2en[zhOf[p.stem]];
      if (e) out[e] += 1.0;
      (p.hiddenStems || []).forEach(function (g, i) {
        var he = zh2en[zhOf[g]];
        if (he) out[he] += (HIDDEN_WEIGHTS[i] || 0.3);
      });
    });
    return out;
  }

  /**
   * 五鼠遁 (Five-Rat Run): derive the hour stem from the day stem and hour branch.
   * Used to override the library's getTimeGan() for the 23:00-24:00 window,
   * where lunar.js computes the time stem from the NEXT day's stem (sect 1)
   * even though the EightChar day pillar stays on the current day (sect 2).
   */
  function fiveRatHourStem(dayStem, hourBranch) {
    var di = GAN.indexOf(dayStem);
    if (di < 0) return '';
    var zi = ZHI.indexOf(hourBranch);
    if (zi < 0) return '';
    // 甲己→甲(0), 乙庚→丙(2), 丙辛→戊(4), 丁壬→庚(6), 戊癸→壬(8)
    var ziStemIdx = (di % 5) * 2;
    return GAN[(ziStemIdx + zi) % 10];
  }

  function assemblePillar(ec, slot, dayStem, isDayPillar) {
    var stem = ask(ec, 'get' + slot + 'Gan', '');
    var hiddenRaw = ask(ec, 'get' + slot + 'HideGan', []);
    var hidden = Array.isArray(hiddenRaw) ? hiddenRaw : [];
    return {
      ganZhi: ask(ec, 'get' + slot, ''),
      stem: stem,
      branch: ask(ec, 'get' + slot + 'Zhi', ''),
      element: ask(ec, 'get' + slot + 'WuXing', ''),
      soundElement: ask(ec, 'get' + slot + 'NaYin', ''),
      stemGod: isDayPillar ? '日主' : tenGod(dayStem, stem),
      hiddenGods: hidden.map(function (g) { return tenGod(dayStem, g); }),
      lifeStage: ask(ec, 'get' + slot + 'DiShi', ''),
      voidBranch: ask(ec, 'get' + slot + 'XunKong', ''),
      hiddenStems: hidden
    };
  }

  /**
   * Day Master strength, and the elements the chart favours.
   * Scoring: season of the month branch counts double, support from the
   * feeding element and from peers, drain from output, wealth and officer.
   * A climatic override runs last: a chart frozen in winter still wants Fire,
   * a chart baked in summer still wants Water, whatever the tally says.
   */
  function judgeStrength(dmElement, monthBranch, tally) {
    var season = BRANCH_SEASON[monthBranch] || 'spring';
    var seasonScore = (SEASON_SCORE[season] || {})[dmElement];
    if (seasonScore === undefined) seasonScore = 2;

    var fedBy = FED_BY[dmElement], feeds = FEEDS[dmElement];
    var controls = CONTROLS[dmElement], curbed = CURBED[dmElement];
    var w = function (e) { return tally[e.toLowerCase()] || 0; };

    var support = w(fedBy) * 1.5 + w(dmElement) * 1.2;
    var drain = w(feeds) * 1.0 + w(controls) * 1.0 + w(curbed) * 1.2;
    var total = seasonScore * 2 + support - drain;

    var verdict, favour, avoid;
    if (total >= 6) {
      verdict = 'strong'; favour = [feeds, controls, curbed]; avoid = [fedBy, dmElement];
    } else if (total <= 2) {
      verdict = 'weak'; favour = [fedBy, dmElement]; avoid = [controls, curbed];
    } else {
      verdict = 'balanced'; favour = [fedBy]; avoid = [];
    }

    var climate = null;
    if (season === 'winter' && ['Wood','Metal','Earth'].indexOf(dmElement) >= 0) climate = 'Fire';
    else if (season === 'summer' && ['Metal','Wood'].indexOf(dmElement) >= 0) climate = 'Water';
    if (climate && favour.indexOf(climate) < 0) favour.unshift(climate);

    var uniq = function (a) {
      var seen = {}, out = [];
      a.forEach(function (x) { if (x && !seen[x]) { seen[x] = 1; out.push(x); } });
      return out;
    };
    /* Seasonal weighting on top of the raw tally: an element that rules the
       month counts for more than its bare count suggests. Used by the
       compatibility scorer, which is why it ships with the chart. */
    var effective = {};
    ['Wood','Fire','Earth','Metal','Water'].forEach(function (e) {
      var sc = (SEASON_SCORE[season] || {})[e] || 0;
      effective[e] = w(e) + (sc >= 4 ? 2 : (sc >= 3 ? 1 : 0));
    });

    return {
      verdict: verdict, season: season, score: Math.round(total * 100) / 100,
      effectiveElements: effective,
      support: Math.round(support * 100) / 100, drain: Math.round(drain * 100) / 100,
      seasonScore: seasonScore, climateNeed: climate,
      favour: uniq(favour), avoid: uniq(avoid),
      roles: { resource: fedBy, companion: dmElement, output: feeds,
               wealth: controls, officer: curbed }
    };
  }

  /**
   * @param {Date} solarTime true solar time, encoded in the UTC fields
   * @param {Date} chinaTime birth instant as China-standard time, in UTC fields
   * @param {string} sex     'male' or 'female'
   */
  function build(solarTime, chinaTime, sex) {
    var S = root.Solar;
    if (!S) throw new Error('calendar library not loaded');

    var solarObj = S.fromYmdHms(
      solarTime.getUTCFullYear(), solarTime.getUTCMonth() + 1, solarTime.getUTCDate(),
      solarTime.getUTCHours(), solarTime.getUTCMinutes(), solarTime.getUTCSeconds()
    );
    var lunarSolar = solarObj.getLunar();
    var ecSolar = lunarSolar.getEightChar();
    ecSolar.setSect(2);

    var cnObj = S.fromYmdHms(
      chinaTime.getUTCFullYear(), chinaTime.getUTCMonth() + 1, chinaTime.getUTCDate(),
      chinaTime.getUTCHours(), chinaTime.getUTCMinutes(), chinaTime.getUTCSeconds()
    );
    var lunarCn = cnObj.getLunar();
    var ecCn = lunarCn.getEightChar();

    /* Day Master always comes from the true-solar day pillar. */
    var dayStem = ecSolar.getDayGan();

    var pillars = {
      year:  assemblePillar(ecCn,    'Year',  dayStem, false),
      month: assemblePillar(ecCn,    'Month', dayStem, false),
      day:   assemblePillar(ecSolar, 'Day',   dayStem, true),
      hour:  assemblePillar(ecSolar, 'Time',  dayStem, false)
    };

    /* Fix: lunar.js sect-2 inconsistency at 23:00–24:00.
       The library computes the hour stem from the NEXT day's stem (sect 1),
       but the day pillar stays on the current day (sect 2).  When the true
       solar hour is 23, re-derive the hour stem from the displayed dayStem
       so the two columns agree.  The hour branch (子) and hidden stems are
       already correct — only the stem needs patching. */
    var solarHour = solarTime.getUTCHours();
    if (solarHour === 23) {
      var correctStem = fiveRatHourStem(dayStem, pillars.hour.branch);
      if (correctStem && correctStem !== pillars.hour.stem) {
        pillars.hour.stem = correctStem;
        pillars.hour.ganZhi = correctStem + pillars.hour.branch;
        pillars.hour.stemGod = tenGod(dayStem, correctStem);
        pillars.hour.element = root.LunarUtil.WU_XING_GAN[correctStem] + root.LunarUtil.WU_XING_ZHI[pillars.hour.branch];
        pillars.hour.soundElement = root.LunarUtil.NAYIN[pillars.hour.ganZhi];
        pillars.hour.voidBranch = root.LunarUtil.getXunKong(pillars.hour.ganZhi);
      }
    }

    /* Luck pillars: the start age derives from the distance to a solar term,
       so this must run on the China-standard basis. */
    var yun = ecCn.getYun(sex === 'male' ? 1 : 0);
    var hourShift = solarHour === 23
      ? root.LunarUtil.getJiaZiIndex(pillars.hour.ganZhi) - root.LunarUtil.getJiaZiIndex(ecCn.getTime()) : 0;
    /* getDaYun()[0] is the stretch from birth until luck actually starts; it carries
       no stem-branch of its own. Dropping it keeps the list to real pillars and stops
       the start age being reported as 1. */
    var raw = yun.getDaYun(11).filter(function (c) {
      return !!ask(c, 'getGanZhi', '');
    }).slice(0, 10);
    var thisYear = new Date().getFullYear();
    var activeIdx = -1;

    var luck = raw.map(function (c, i) {
      var from = ask(c, 'getStartYear', 0), to = ask(c, 'getEndYear', 0);
      if (thisYear >= from && thisYear <= to) activeIdx = i;
      var years = [];
      try {
        var ln = c.getLiuNian(), xy = c.getXiaoYun();
        years = ln.map(function (y, j) {
          var minor = xy[j] ? ask(xy[j], 'getGanZhi', '') : '';
          if (hourShift && minor) {
            minor = root.LunarUtil.JIA_ZI[(root.LunarUtil.getJiaZiIndex(minor) + hourShift + 60) % 60];
          }
          return {
            year: ask(y, 'getYear', 0),
            age: ask(y, 'getAge', 0),
            ganZhi: ask(y, 'getGanZhi', ''),
            minorGanZhi: minor
          };
        });
      } catch (e) { /* some ranges carry no annual detail */ }
      var gz = ask(c, 'getGanZhi', '');
      return {
        ganZhi: gz, stem: gz[0] || '', branch: gz[1] || '',
        startAge: ask(c, 'getStartAge', 0), endAge: ask(c, 'getEndAge', 0),
        startYear: from, endYear: to, years: years
      };
    });

    var forward = true;
    if (typeof yun.isForward === 'function') forward = yun.isForward();
    else if (typeof yun.isShun === 'function') forward = yun.isShun();

    var active = null;
    if (activeIdx >= 0) {
      active = Object.assign({}, luck[activeIdx], { index: activeIdx, current: true });
    } else if (luck.length) {
      active = Object.assign({}, luck[0], { index: 0, current: false, pending: true });
    }

    var thisYearPillar = null;
    if (active && active.years) {
      for (var i = 0; i < active.years.length; i++) {
        if (active.years[i].year === thisYear) { thisYearPillar = active.years[i]; break; }
      }
    }
    if (!thisYearPillar) {
      try {
        var gz2 = S.fromYmd(thisYear, 6, 1).getLunar().getYearInGanZhi();
        thisYearPillar = { year: thisYear, ganZhi: gz2, stem: gz2[0] || '', branch: gz2[1] || '' };
      } catch (e) {
        thisYearPillar = { year: thisYear, ganZhi: '', stem: '', branch: '' };
      }
    }

    var tally = tallyElements(pillars);
    var strength = judgeStrength(stemElement(dayStem), pillars.month.branch, tally);

    /* Auspicious/inauspicious stars, if the table module is present. It is
       optional on purpose: a missing table costs one block, never the chart. */
    var stars = [];
    if (root.StarTable && typeof root.StarTable.compute === 'function') {
      try {
        stars = root.StarTable.compute(['year','month','day','hour'].map(function (k) {
          return [pillars[k].stem, pillars[k].branch];
        })) || [];
      } catch (e) { stars = []; }
    }

    return {
      dayMaster: dayStem,
      dayMasterElement: stemElement(dayStem),
      dayMasterPolarity: stemPolarity(dayStem),
      strength: strength,
      stars: stars,
      pillars: pillars,
      elements: tally,
      luck: luck,
      luckStart: {
        age: luck.length ? luck[0].startAge : 0,
        year: luck.length ? luck[0].startYear : 0,
        forward: forward,
        /* Exact offset from birth to the first luck pillar. */
        afterYears: ask(yun, 'getStartYear', 0),
        afterMonths: ask(yun, 'getStartMonth', 0),
        afterDays: ask(yun, 'getStartDay', 0)
      },
      activeLuck: active,
      activeYear: thisYearPillar,
      palaces: {
        conception: ask(ecCn, 'getTaiYuan', ''),
        destiny: ask(ecCn, 'getMingGong', ''),
        body: ask(ecCn, 'getShenGong', '')
      },
      animals: {
        year: ask(lunarCn, 'getYearShengXiao', ''),
        month: ask(lunarCn, 'getMonthShengXiao', ''),
        day: ask(lunarSolar, 'getDayShengXiao', ''),
        hour: ask(lunarSolar, 'getTimeShengXiao', '')
      },
      dayAlmanac: {
        favourable: (ask(lunarSolar, 'getDayYi', []) || []).join(', '),
        avoid: (ask(lunarSolar, 'getDayJi', []) || []).join(', '),
        clash: ask(lunarSolar, 'getDayChongShengXiao', ''),
        evil: ask(lunarSolar, 'getDaySha', '')
      },
      solarDate: solarObj.toYmdHms(),
      lunarDate: lunarSolar.toString(),
      meta: { sex: sex, solarISO: solarTime.toISOString(), chinaISO: chinaTime.toISOString(), version: 'midnight-hour-consistency-20261020' }
    };
  }

  root.ChartEngine = {
    build: build,
    tenGod: tenGod,
    stemElement: stemElement,
    stemPolarity: stemPolarity
  };
})(window);
